import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { FiRefreshCw, FiUser } from 'react-icons/fi';
import { IAppointmentLog, getAppointmentLogs } from '../../services/api-graphql-calls';
import {
  Container,
  TagButton,
  CountBadge,
  Dropdown,
  LogItem,
  LogDescription,
  LogTypeBadge,
  LogMeta,
  EmptyMessage,
} from './styles';

interface IAppointmentHistoryTagProps {
  appointmentId: string;
}

type LogType = 'reschedule' | 'professional' | 'other';

interface IParsedLog extends IAppointmentLog {
  logType: LogType;
}

const DROPDOWN_HEIGHT_ESTIMATE = 320;
const DROPDOWN_OFFSET = 8;

const formatDateTime = (value: string): string => {
  const date = new Date(value);
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const detectLogType = (description: string): LogType => {
  const lower = description.toLowerCase();
  if (lower.includes('profissional de')) return 'professional';
  if (lower.includes('período de') || lower.includes('data de')) return 'reschedule';
  return 'other';
};

const AppointmentHistoryTag: React.FC<IAppointmentHistoryTagProps> = ({ appointmentId }) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [logs, setLogs] = useState<IParsedLog[] | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number; placement: 'top' | 'bottom' } | null>(null);

  useEffect(() => {
    getAppointmentLogs(appointmentId)
      .then((data) => {
        console.log('[AppointmentHistoryTag] logs recebidos:', data);
        setLogs(data.map((log) => ({ ...log, logType: detectLogType(log.description) })));
      })
      .catch((err) => {
        console.error('[AppointmentHistoryTag] Erro ao buscar logs:', err);
        setLogs([]);
      });
  }, [appointmentId]);

  const rescheduleCount = logs?.filter((l) => l.logType === 'reschedule').length ?? 0;
  const professionalCount = logs?.filter((l) => l.logType === 'professional').length ?? 0;

  const handleClick = () => {
    if (pos) { setPos(null); return; }
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const placement = rect.top >= DROPDOWN_HEIGHT_ESTIMATE ? 'top' : 'bottom';
    setPos({
      top: placement === 'top'
        ? rect.top + window.scrollY - DROPDOWN_OFFSET
        : rect.bottom + window.scrollY + DROPDOWN_OFFSET,
      left: rect.left + window.scrollX + rect.width / 2,
      placement,
    });
  };

  const handleBlur = (e: React.FocusEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) setPos(null);
  };

  const logTypeLabel: Record<LogType, string> = {
    reschedule: 'Reagendamento',
    professional: 'Troca de profissional',
    other: 'Alteração',
  };

  return (
    <Container onBlur={handleBlur}>
      <TagButton ref={buttonRef} type="button" onClick={handleClick}>
        Histórico
        {rescheduleCount > 0 && (
          <CountBadge variant="reschedule">
            <FiRefreshCw size={9} />
            {rescheduleCount}
          </CountBadge>
        )}
        {professionalCount > 0 && (
          <CountBadge variant="professional">
            <FiUser size={9} />
            {professionalCount}
          </CountBadge>
        )}
      </TagButton>

      {pos && logs !== null && ReactDOM.createPortal(
        <Dropdown $top={pos.top} $left={pos.left} $placement={pos.placement}>
          {logs.length === 0 && <EmptyMessage>Nenhum registro encontrado.</EmptyMessage>}
          {logs.map((log, index) => (
            <LogItem key={index}>
              {log.logType !== 'other' && (
                <LogTypeBadge variant={log.logType}>
                  {log.logType === 'reschedule' && <FiRefreshCw size={10} />}
                  {log.logType === 'professional' && <FiUser size={10} />}
                  {logTypeLabel[log.logType]}
                </LogTypeBadge>
              )}
              <LogDescription>{log.description}</LogDescription>
              <LogMeta>
                {formatDateTime(log.created_at)}
                {log.user ? ` · ${log.user.name}` : ''}
              </LogMeta>
            </LogItem>
          ))}
        </Dropdown>,
        document.body,
      )}
    </Container>
  );
};

export default AppointmentHistoryTag;
